/**
 * Das Arbeits-PDF als zweite Spalte neben dem Editor – Mitschreiben und im
 * Material markieren, ohne zwischen zwei Seiten zu wechseln.
 *
 * Bewusst zurückhaltend gebaut:
 *   - Alles (pdf-lib + der Betrachter, zusammen gut 600 kB) wird erst beim
 *     ersten Öffnen nachgeladen; wer die Spalte nie aufmacht, zahlt nichts.
 *   - Der Betrachter speichert selbst weiter (siehe DocumentViewer), und beim
 *     Schließen oder Verlassen der Seite wird noch einmal geschrieben.
 *   - Auf schmalen Schirmen gibt es die Spalte nicht: zwei Arbeitsflächen
 *     nebeneinander sind dort nicht bedienbar.
 *
 * Editor und Betrachter schreiben in getrennte Sammlungen (Notiz bzw.
 * Materialien) – sie können sich also nicht gegenseitig überschreiben.
 */

import { h, clear } from "@/lib/dom";
import { button, toast, spinner } from "@/components/ui";
import { pbErrorMessage } from "@/services";

type Viewer = import("@/components/DocumentViewer").DocumentViewer;
type TopicPdf = import("@/services/document/topicPdf").TopicPdf;
type PageTemplate = import("@/services/document/topicPdf").PageTemplate;

/**
 * Eine Stelle im Arbeits-PDF, unabhängig von dessen Zusammenstellung: die
 * Datei selbst und die Seite **innerhalb** dieser Datei. Nur so bleibt ein
 * Verweis gültig, wenn später Dateien dazukommen, wegfallen oder umsortiert
 * werden.
 */
export interface PdfRef {
  material: string;
  page: number;
  label: string;
}

export interface PdfPanel {
  el: HTMLElement;
  isOpen(): boolean;
  toggle(): Promise<boolean>;
  /** Speichert Offenes und baut den Betrachter ab. */
  close(): Promise<void>;
  /** Wo steht der Betrachter gerade? (für „PDF-Seite einfügen") */
  currentRef(): PdfRef | null;
  /** Anfang des Seitentexts der gerade sichtbaren Seite (leer bei Scans). */
  currentHeading(): Promise<string>;
  /** Springt zu einer gemerkten Stelle; öffnet die Spalte bei Bedarf. */
  showRef(ref: { material: string; page: number }): Promise<boolean>;
}

/** Zwei Arbeitsflächen nebeneinander lohnen erst ab dieser Breite. */
export function pdfPanelFits(): boolean {
  return window.matchMedia("(min-width: 1000px)").matches;
}

export function createPdfPanel(topicId: string): PdfPanel {
  const body = h("div", { class: "editor-pdf-body" });
  const el = h("div", { class: "editor-pdf" }, body);

  let viewer: Viewer | null = null;
  let built: TopicPdf | null = null;
  let loading = false;
  let saving = false;
  // Zugeklappt (Knopf/Breakpoint) heißt nur "gerade nicht sichtbar", nicht
  // "weg": viewer/built bleiben im Speicher, ein Wiederaufklappen hängt nur
  // dieselbe DOM-Node erneut ein statt Materialien neu zu laden und das PDF
  // neu zusammenzubauen. Nur das echte Verlassen der Editor-Seite (close(),
  // von EditorPage.cleanup() aufgerufen) baut wirklich ab.
  let attached = false;
  // Siehe DocumentPage.ts: sonst geht eine Änderung verloren, die während
  // eines laufenden Uploads dazukommt (früher: `if (saving) return`).
  let resaveQueued = false;
  let currentPage = 1;

  async function persist(pdf: Blob): Promise<void> {
    if (!built || !viewer) return;
    if (saving) {
      resaveQueued = true;
      return;
    }
    saving = true;
    try {
      // Schreibt nur aufs Gerät (localFiles.ts), hochgeladen wird im Hintergrund.
      const { saveTopicPdf, rememberSaved } = await import("@/services/document/topicPdf");
      const rotations = viewer.rotations();
      const written = await saveTopicPdf(built, pdf, viewer.changedPages(), rotations);
      if (written.length && !rotations.size) rememberSaved(built, pdf);
      viewer.clearDirty();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    } finally {
      saving = false;
      if (resaveQueued) {
        resaveQueued = false;
        void saveIfNeeded();
      }
    }
  }

  async function saveIfNeeded(): Promise<void> {
    if (!viewer?.isDirty) return;
    if (saving) {
      resaveQueued = true;
      return;
    }
    try {
      await persist(await viewer.save());
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  // Anders als die volle PDF-Seite (DocumentPage.ts) hatte die Spalte hier
  // bisher GAR KEINE Absicherung gegen Reload/Tab-Wechsel – geschrieben wurde
  // nur beim expliziten Zuklappen. Ein Reload bei offener Spalte hat jede
  // frisch gezeichnete Markierung ersatzlos verworfen.
  const onHidden = () => {
    if (document.visibilityState === "hidden") void saveIfNeeded();
  };
  const onLeaveHard = () => void saveIfNeeded();
  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!viewer?.isDirty) return;
    void saveIfNeeded();
    e.preventDefault();
    e.returnValue = "";
  };
  function watchUnload(): void {
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onLeaveHard);
    window.addEventListener("beforeunload", onBeforeUnload);
  }
  function unwatchUnload(): void {
    document.removeEventListener("visibilitychange", onHidden);
    window.removeEventListener("pagehide", onLeaveHard);
    window.removeEventListener("beforeunload", onBeforeUnload);
  }

  /** "+"-Knopf im Dreh-Panel: siehe DocumentPage.ts, gleiches Vorgehen. */
  async function addPage(afterPage: number, kind: PageTemplate): Promise<void> {
    if (!built) return;
    const origin = built.origins.find((o) => o.pageIndex === afterPage - 1);
    const material = origin ? built.sources[origin.sourceIndex] : undefined;
    if (!origin || !material) {
      toast("Konnte die aktuelle Seite keiner Datei zuordnen.", "error");
      return;
    }
    try {
      await saveIfNeeded();
      const { insertTemplatePage } = await import("@/services/document/topicPdf");
      await insertTemplatePage(topicId, built.sources, material.id, kind);
      const lastPageIndexOfSource = Math.max(
        ...built.origins.filter((o) => o.sourceIndex === origin.sourceIndex).map((o) => o.pageIndex)
      );
      writePage(topicId, lastPageIndexOfSource + 2);
      await close();
      await open();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** "Text/OCR kopieren" im Dateien-Menü: Material der gerade sichtbaren Seite. */
  async function copyTextOfCurrentPage(): Promise<void> {
    if (!built) return;
    const origin = built.origins.find((o) => o.pageIndex === currentPage - 1);
    const material = origin ? built.sources[origin.sourceIndex] : undefined;
    if (!material) {
      toast("Konnte die aktuelle Seite keiner Datei zuordnen.", "error");
      return;
    }
    const { openPdfTextDialog } = await import("@/features/materials/pdfTextDialog");
    openPdfTextDialog(material);
  }

  async function open(): Promise<boolean> {
    if (loading) return false;
    loading = true;
    clear(body);
    const statusText = h("span", null, "PDF wird gebaut …");
    const progressBar = h("div", { class: "upload-progress-bar" });
    const status = h(
      "div",
      { class: "editor-pdf-status" },
      spinner(),
      statusText,
      h("div", { class: "upload-progress-track is-visible" }, progressBar)
    );
    body.appendChild(status);
    try {
      const [{ DocumentViewer }, { buildTopicPdf }, { materialsService }, { offlineSync }] = await Promise.all([
        import("@/components/DocumentViewer"),
        import("@/services/document/topicPdf"),
        import("@/services"),
        import("@/services/offline/offline-sync.service"),
      ]);
      // Was auf dem Gerät liegt, sofort – die Serverliste nur, wenn das Thema
      // hier noch nie geöffnet war. Abgeglichen wird danach (refreshInBackground).
      const local = await offlineSync.materialsLocal(topicId).catch(() => null);
      const list = local?.length ? local : await materialsService.listByTopic(topicId);
      built = await buildTopicPdf(list, ({ message, fraction }) => {
        statusText.textContent = message;
        progressBar.style.width = `${Math.round(fraction * 100)}%`;
      });
      viewer = new DocumentViewer({
        source: built.blob,
        annotationTexts: built.annotationTexts,
        namedDests: built.namedDests,
        linkTargets: built.linkTargets,
        initialPage: readPage(topicId),
        onSaveRequested: (pdf) => void persist(pdf),
        onPageChange: (page) => {
          currentPage = page;
          writePage(topicId, page);
        },
        // Gedrehte Seiten stecken erst nach dem Speichern in den Materialien
        // – Panel zu und mit frischen Daten wieder auf, statt die ganze
        // Seite neu zu laden.
        onReloadNeeded: () => void close().then(() => open()),
        onAddPage: (page, kind) => void addPage(page, kind),
        onCopyText: () => void copyTextOfCurrentPage(),
      });
      clear(body);
      body.appendChild(viewer.root);
      attached = true;
      await viewer.mount();
      watchUnload();
      const { warmPageHeadings } = await import("@/services/source.service");
      if (built) warmPageHeadings(built.blob);
      if (local?.length) void refreshInBackground();
      return true;
    } catch (err) {
      viewer = null;
      built = null;
      attached = false;
      clear(body);
      body.appendChild(
        h(
          "div",
          { class: "editor-pdf-status error" },
          h("p", null, err instanceof Error ? err.message : pbErrorMessage(err)),
          button({ label: "Erneut versuchen", size: "sm", onClick: () => void open() })
        )
      );
      return false;
    } finally {
      loading = false;
    }
  }

  /**
   * Serverstand nachziehen, ohne dass das Öffnen darauf gewartet hat. Neu
   * aufgebaut wird nur, wenn sich wirklich etwas geändert hat (anderes Gerät)
   * und hier gerade nichts Ungespeichertes offen ist.
   */
  async function refreshInBackground(): Promise<void> {
    try {
      const [{ materialsService }, { offlineSync }, { pullTopic }, { isMedia }] = await Promise.all([
        import("@/services"),
        import("@/services/offline/offline-sync.service"),
        import("@/services/document/fileSync"),
        import("@/services/document/topicPdf"),
      ]);
      const server = await materialsService.listByTopic(topicId);
      await offlineSync.saveMaterials(topicId, server);
      const changed = await pullTopic(topicId, server);
      const list = await offlineSync.materialsLocal(topicId);
      const ids = (l: { id: string }[]) => l.map((m) => m.id).join();
      const sameFiles = list && built && ids(list.filter((m) => !isMedia(m))) === ids(built.sources);
      if (!changed && sameFiles) return;
      if (!viewer || viewer.isDirty || saving) return;
      await close();
      await open();
    } catch {
      /* offline oder Server weg – der lokale Stand bleibt einfach stehen */
    }
  }

  /**
   * Nur ausblenden – viewer/built bleiben, siehe `attached`-Kommentar oben.
   * Bewusst NICHT aus dem DOM entfernen (kein `clear(body)`): EmbedPDF
   * initialisiert sich in `mount()` genau einmal gegen das damals verbundene
   * `viewer.root` – wird die Node danach ab- und wieder angehängt, verliert
   * der Betrachter seinen Zustand und zeigt "No Documents Open" statt des
   * PDFs. Nur die CSS-Anzeige umschalten hält die Node durchgehend verbunden.
   * Inline-Style statt `hidden`-Attribut, weil `.dv-root` bereits ein eigenes
   * `display: flex` setzt (gleiche Spezifität wie `[hidden]`, würde es sonst
   * je nach Regel-Reihenfolge überstimmen).
   */
  function hide(): void {
    if (!attached || !viewer) return;
    viewer.root.style.display = "none";
    attached = false;
  }

  /** Wieder sichtbar machen, ohne neu zu bauen. */
  function show(): void {
    if (attached || !viewer) return;
    viewer.root.style.display = "";
    attached = true;
  }

  /** Echtes Abbauen – nur beim Verlassen der Editor-Seite (EditorPage.cleanup()). */
  async function close(): Promise<void> {
    if (!viewer) return;
    unwatchUnload();
    await saveIfNeeded();
    viewer.destroy();
    viewer = null;
    built = null;
    attached = false;
    clear(body);
  }

  /** Seite des Arbeitsdokuments -> Datei + Seite darin. */
  function refOf(page: number): PdfRef | null {
    if (!built) return null;
    const origin = built.origins.find((o) => o.pageIndex === page - 1);
    const material = origin && built.sources[origin.sourceIndex];
    if (!origin || !material) return null;
    return {
      material: material.id,
      page: origin.sourcePageIndex + 1,
      label: material.title,
    };
  }

  return {
    el,
    isOpen: () => attached || loading,
    currentRef: () => refOf(currentPage),
    async currentHeading(): Promise<string> {
      if (!built) return "";
      const { pageHeading } = await import("@/services/source.service");
      return pageHeading(built.blob, currentPage);
    },
    async showRef(ref): Promise<boolean> {
      if (!viewer) {
        if (!(await open())) return false;
      } else if (!attached) {
        show();
      }
      if (!built) return false;
      // Rückweg: Datei + Seite darin -> Seite im aktuellen Arbeitsdokument.
      // Ein Verweis von vor dem Upload trägt noch die lokale ID (localFiles.ts).
      const { resolveFileId } = await import("@/services/document/localFiles");
      const id = resolveFileId(ref.material);
      const index = built.sources.findIndex((m) => m.id === id || m.id === ref.material);
      const origin = built.origins.find(
        (o) => o.sourceIndex === index && o.sourcePageIndex === ref.page - 1
      );
      if (index < 0 || !origin) return false;
      viewer?.showPage(origin.pageIndex + 1);
      return true;
    },
    async toggle(): Promise<boolean> {
      if (attached) {
        hide();
        return false;
      }
      if (viewer) {
        show();
        return true;
      }
      if (loading) return false;
      return open();
    },
    close,
  };
}

/* Dieselbe Merkstelle wie die volle PDF-Seite – wer dort weiterliest, landet
   hier an derselben Stelle. */
function pageKey(topicId: string): string {
  return `doc_page_${topicId}`;
}
function readPage(topicId: string): number {
  const raw = Number(localStorage.getItem(pageKey(topicId)));
  return Number.isFinite(raw) && raw >= 1 ? raw : 1;
}
function writePage(topicId: string, page: number): void {
  try {
    localStorage.setItem(pageKey(topicId), String(page));
  } catch {
    /* ignore */
  }
}
